import { useEffect } from 'react';

/**
 * Freeze the page behind an overlay — for real, on iOS too.
 *
 * `document.body.style.overflow = 'hidden'` is the usual move and it is NOT a lock on iOS:
 * the scrolling element there is <html>, so the document keeps scrolling under a fixed
 * overlay. On the event page's GET TICKETS sheet that meant every tap on the sheet dragged
 * the page behind it a few pixels, which iOS reads as a scroll gesture and cancels the
 * click — the button looked dead. (Same reasoning as `.app-locked` in index.css, which
 * pins the full-viewport messages route.)
 *
 * This pins the BODY at `top: -scrollY` instead, so there is nothing left to scroll, and
 * puts the page back exactly where it was on release. `.app-locked` routes already have
 * nothing to scroll, so the lock stands aside there rather than fighting it.
 *
 * `position: fixed` on body is deliberately safe HERE even though the messages route
 * refuses it: the overlays that use this (BottomSheet) lay themselves out in the VISUAL
 * viewport, so when iOS shifts that viewport to reveal a focused field the sheet moves
 * with it instead of being thrown off the top.
 *
 * Nest-safe — a counter means closing an inner overlay doesn't unfreeze the page under an
 * outer one that is still open.
 */
let lockCount = 0;
let saved = null;

function apply() {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  // A full-viewport route is already pinned; touching body would only fight it.
  if (root.classList.contains('app-locked')) { saved = { skipped: true }; return; }
  const body = document.body;
  // Someone else owns the lock already — a vaul/Radix drawer pins the body the same way,
  // and by then `window.scrollY` reads 0, so re-pinning would stamp `top: 0` over their
  // offset and snap the page behind the drawer to the top (and back again on close).
  if (getComputedStyle(body).position === 'fixed') { saved = { skipped: true }; return; }
  const y = window.scrollY || window.pageYOffset || 0;
  // Taking body out of flow drops the desktop scrollbar; hold its width so nothing shifts.
  const gap = Math.max(0, window.innerWidth - root.clientWidth);
  saved = {
    y,
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
    width: body.style.width,
    overflow: body.style.overflow,
    overscrollBehavior: body.style.overscrollBehavior,
    paddingRight: body.style.paddingRight,
  };
  body.style.position = 'fixed';
  body.style.top = `${-y}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
  body.style.overflow = 'hidden';
  body.style.overscrollBehavior = 'none';
  if (gap) body.style.paddingRight = `${gap}px`;
}

function release() {
  if (!saved) return;
  const was = saved;
  saved = null;
  if (was.skipped || typeof document === 'undefined') return;
  const body = document.body;
  body.style.position = was.position;
  body.style.top = was.top;
  body.style.left = was.left;
  body.style.right = was.right;
  body.style.width = was.width;
  body.style.overflow = was.overflow;
  body.style.overscrollBehavior = was.overscrollBehavior;
  body.style.paddingRight = was.paddingRight;
  // Only when there is somewhere to go back to — jsdom has no real scrolling.
  if (was.y) window.scrollTo(0, was.y);
}

export function lockPageScroll() {
  lockCount += 1;
  if (lockCount === 1) apply();
}

export function unlockPageScroll() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) release();
}

export default function usePageScrollLock(active = true) {
  useEffect(() => {
    if (!active) return undefined;
    lockPageScroll();
    return unlockPageScroll;
  }, [active]);
}
